import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaUserMd, FaPills, FaPlus, FaSearch, FaEdit, FaTrash, FaCalendarAlt, FaEye } from 'react-icons/fa';
import tratamientoService from '../services/tratamientoService';
import { sociasApiService } from '../services/sociasApiService';
import '../styles/InventoryManagement.css';

const TratamientoManagement = ({ createMode = false }) => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // Obtener datos del paciente desde la navegación (si viene desde PartnersList)
    const { pacienteId = null, pacienteNombre = null, fromPatientDetail = false } = location.state || {};

    // Estados para datos de la API
    const [tratamientos, setTratamientos] = useState([]);
    const [medicos, setMedicos] = useState([]);
    const [pacientes, setPacientes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [currentTratamiento, setCurrentTratamiento] = useState(null);
    const [showForm, setShowForm] = useState(createMode);
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewTratamiento, setViewTratamiento] = useState(null);
    
    const [formData, setFormData] = useState({
        MEDICO_ID: '',
        PACIENTE_ID: pacienteId || '',
        TRATAMIENTO_FECHA_INICIO: '',
        TRATAMIENTO_FECHA_FIN: '',
        TRATAMIENTO_DESCRIPCION: ''
    });
    
    // Estado separado para medicamentos (ahora soporta múltiples)
    const [medicamentos, setMedicamentos] = useState([
        { id: null, nombre: '', cantidad: '', unidad: 'mg' }
    ]);
    
    // Estado para rastrear medicamentos eliminados (solo sus IDs)
    const [medicamentosEliminados, setMedicamentosEliminados] = useState([]);
    
    const [errors, setErrors] = useState({});

    // Estados de paginación
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPagesServer, setTotalPagesServer] = useState(1);
    const [serverSide, setServerSide] = useState(false);

    // Cargar metadatos (médicos y pacientes)
    const loadMetaData = useCallback(async () => {
        try {
            const medicosRes = await tratamientoService.getMedicosDisponibles();
            if (medicosRes.success) setMedicos(medicosRes.data || []);

            try {
                const sociasData = await sociasApiService.getSocias();
                const pacientesData = sociasData.map(socia => ({
                    PACIENTE_ID: socia.sociaId,
                    PACIENTE_NOMBRE: `${socia.sociaNombre || ''} ${socia.sociaApellido || ''}`.trim(),
                    PACIENTE_CEDULA: socia.sociaCedula || '',
                    PACIENTE_ESTADO: socia.sociaEstado || 'Activo'
                }));
                setPacientes(pacientesData);
            } catch (sociaErr) {
                console.error('Error al cargar socias desde API:', sociaErr);
                setPacientes([]);
            }
        } catch (err) {
            console.error('Error cargando metadatos:', err);
        }
    }, []);

    // Cargar tratamientos, usa server-side pagination cuando NO hay pacienteId
    const fetchTratamientos = useCallback(async () => {
        setLoading(true);
        try {
            if (pacienteId) {
                const tratamientosRes = await tratamientoService.getTratamientosBySocia(pacienteId);
                if (tratamientosRes.success) {
                    setServerSide(false);
                    setTratamientos(tratamientosRes.data || []);
                    setTotalItems((tratamientosRes.data || []).length);
                } else {
                    console.error('Error al cargar tratamientos por socia:', tratamientosRes.message);
                    setTratamientos([]);
                }
            } else {
                // Server-side paginated request
                const res = await tratamientoService.getTratamientos(currentPage, itemsPerPage, searchTerm);
                if (res.success) {
                    const payload = res.data;
                    // Si el backend devolvió el arreglo legacy (sin paginación)
                    if (Array.isArray(payload)) {
                        setServerSide(false);
                        setTratamientos(payload || []);
                        setTotalItems((payload || []).length);
                        setTotalPagesServer(Math.max(1, Math.ceil((payload || []).length / itemsPerPage)));
                    } else if (payload && payload.data) {
                        setServerSide(true);
                        setTratamientos(payload.data || []);
                        setTotalItems(payload.total || 0);
                        setTotalPagesServer(payload.totalPages || Math.max(1, Math.ceil((payload.total || 0) / (payload.pageSize || itemsPerPage))));
                        setCurrentPage(payload.page || 1);
                        setItemsPerPage(payload.pageSize || itemsPerPage);
                    } else {
                        // Fallback
                        setServerSide(false);
                        setTratamientos([]);
                        setTotalItems(0);
                    }
                } else {
                    console.error('Error al solicitar tratamientos paginados:', res.message);
                    setTratamientos([]);
                }
            }
        } catch (err) {
            console.error('Error al obtener tratamientos:', err);
            setTratamientos([]);
        } finally {
            setLoading(false);
        }
    }, [pacienteId, currentPage, itemsPerPage, searchTerm]);

    // Cargar metadatos una vez
    useEffect(() => {
        loadMetaData();
    }, [loadMetaData]);

    // Cargar tratamientos cuando cambian página/filtrado/paciente
    useEffect(() => {
        fetchTratamientos();
    }, [fetchTratamientos]);

    // Si hay un paciente específico, pre-seleccionarlo en el formulario
    useEffect(() => {
        if (pacienteId && pacienteNombre) {
            setFormData(prev => ({
                ...prev,
                PACIENTE_ID: pacienteId
            }));
        }
    }, [pacienteId, pacienteNombre]);

    // Obtener nombre de paciente
    // const _getMedicoNombre = (id) => {
    //     const medico = medicos.find(m => m.MEDICO_ID === id);
    //     return medico ? `${medico.MEDICO_NOMBRE} (${medico.MEDICO_ESPECIALIDAD})` : 'Médico no encontrado';
    // };

    // Obtener nombre de paciente
    // const _getPacienteNombre = (id) => {
    //     const paciente = pacientes.find(p => p.PACIENTE_ID === id);
    //     return paciente ? paciente.PACIENTE_NOMBRE : 'Paciente no encontrado';
    // };

    // Filtrar solo en cliente si no estamos usando paginación server-side
    const filteredTratamientos = serverSide ? tratamientos : tratamientos.filter(tratamiento => {
        const searchLower = searchTerm.toLowerCase();
        return (
            (tratamiento.TRATAMIENTO_ID || '').toLowerCase().includes(searchLower) ||
            (tratamiento.MEDICO_NOMBRE || '').toLowerCase().includes(searchLower) ||
            (tratamiento.PACIENTE_NOMBRE || '').toLowerCase().includes(searchLower) ||
            (tratamiento.MEDICAMENTO_NOMBRE || '').toLowerCase().includes(searchLower) ||
            (tratamiento.TRATAMIENTO_DESCRIPCION || '').toLowerCase().includes(searchLower)
        );
    });

    // Paginación (cliente) si no server-side
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = serverSide ? tratamientos : filteredTratamientos.slice(indexOfFirstItem, indexOfFirstItem + itemsPerPage);
    const totalPages = serverSide ? totalPagesServer : Math.max(1, Math.ceil(filteredTratamientos.length / itemsPerPage));

    // Validar formulario
    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.MEDICO_ID) {
            newErrors.MEDICO_ID = 'Debe seleccionar un médico';
        }
        if (!formData.PACIENTE_ID) {
            newErrors.PACIENTE_ID = 'Debe seleccionar un paciente';
        }
        
        // Validar TODOS los medicamentos
        medicamentos.forEach((med, index) => {
            if (!med.nombre.trim()) {
                newErrors[`medicamento_nombre_${index}`] = 'El nombre del medicamento es requerido';
            }
            if (!med.cantidad || med.cantidad <= 0) {
                newErrors[`medicamento_cantidad_${index}`] = 'La cantidad debe ser mayor a 0';
            }
        });
        
        if (!formData.TRATAMIENTO_FECHA_INICIO) {
            newErrors.TRATAMIENTO_FECHA_INICIO = 'La fecha de inicio es requerida';
        } else {
            // Validar que la fecha de inicio no sea anterior al día actual (solo para nuevos tratamientos)
            if (!currentTratamiento) {
                const hoy = new Date();
                hoy.setHours(0, 0, 0, 0);
                const fechaInicio = new Date(formData.TRATAMIENTO_FECHA_INICIO);
                fechaInicio.setHours(0, 0, 0, 0);
                
                if (fechaInicio < hoy) {
                    newErrors.TRATAMIENTO_FECHA_INICIO = 'La fecha de inicio no puede ser anterior al día actual';
                }
            }
        }
        if (!formData.TRATAMIENTO_FECHA_FIN) {
            newErrors.TRATAMIENTO_FECHA_FIN = 'La fecha de fin es requerida';
        }
        
        // Validar que fecha de fin sea posterior a fecha de inicio
        if (formData.TRATAMIENTO_FECHA_INICIO && formData.TRATAMIENTO_FECHA_FIN) {
            if (new Date(formData.TRATAMIENTO_FECHA_FIN) <= new Date(formData.TRATAMIENTO_FECHA_INICIO)) {
                newErrors.TRATAMIENTO_FECHA_FIN = 'La fecha de fin debe ser posterior a la fecha de inicio';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Manejar cambios en el formulario
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Limpiar error del campo
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Agregar nuevo medicamento
    const handleAddMedicamento = () => {
        setMedicamentos([...medicamentos, { id: null, nombre: '', cantidad: '', unidad: 'mg' }]);
    };

    // Eliminar medicamento
    const handleRemoveMedicamento = (index) => {
        if (medicamentos.length > 1) {
            const medicamentoAEliminar = medicamentos[index];
            
            // Si el medicamento tiene ID (existe en la BD), agregarlo a la lista de eliminados
            if (medicamentoAEliminar.id) {
                setMedicamentosEliminados(prev => [...prev, medicamentoAEliminar.id]);
            }
            
            const newMedicamentos = medicamentos.filter((_, i) => i !== index);
            setMedicamentos(newMedicamentos);
            // Limpiar errores de ese medicamento
            const newErrors = { ...errors };
            delete newErrors[`medicamento_nombre_${index}`];
            delete newErrors[`medicamento_cantidad_${index}`];
            setErrors(newErrors);
        }
    };

    // Manejar cambios en medicamentos
    const handleMedicamentoChange = (index, field, value) => {
        const newMedicamentos = [...medicamentos];
        newMedicamentos[index][field] = value;
        setMedicamentos(newMedicamentos);
        
        // Limpiar error del campo
        const errorKey = `medicamento_${field}_${index}`;
        if (errors[errorKey]) {
            setErrors(prev => ({
                ...prev,
                [errorKey]: ''
            }));
        }
    };

    // Manejar envío del formulario
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        setLoading(true);
        try {
            // Preparar TODOS los medicamentos CON SUS IDs (si existen)
            const medicamentosData = medicamentos.map(med => {
                const medicamentoObj = {
                    MEDICAMENTO_NOMBRE: med.nombre,
                    MEDICAMENTO_CANTIDAD: parseInt(med.cantidad) || 0,
                    MEDICAMENTO_UNIDAD: med.unidad || 'mg'
                };
                
                // ⚠️ IMPORTANTE: Si tiene ID, es un medicamento existente (actualizar)
                // Si no tiene ID, es un medicamento nuevo (crear)
                if (med.id) {
                    medicamentoObj.MEDICAMENTO_ID = med.id;
                }
                
                return medicamentoObj;
            });

            const tratamientoData = {
                SOCIA_ID: formData.PACIENTE_ID,
                MEDICO_ID: formData.MEDICO_ID,
                TRATAMIENTO_FECHAINICIO: formData.TRATAMIENTO_FECHA_INICIO,
                TRATAMIENTO_FECHAFIN: formData.TRATAMIENTO_FECHA_FIN,
                TRATAMIENTO_OBSERVACION: formData.TRATAMIENTO_DESCRIPCION,
                MEDICAMENTOS: medicamentosData
            };

            // Si estamos editando y hay medicamentos eliminados, incluirlos
            if (currentTratamiento && medicamentosEliminados.length > 0) {
                tratamientoData.MEDICAMENTOS_ELIMINADOS = medicamentosEliminados;
            }

            let result;
            if (currentTratamiento) {
                // Actualizar tratamiento existente
                result = await tratamientoService.updateTratamiento(
                    currentTratamiento.TRATAMIENTO_ID, 
                    tratamientoData
                );
            } else {
                // Crear nuevo tratamiento
                result = await tratamientoService.createTratamiento(tratamientoData);
            }

            if (result.success) {
                alert(result.message);
                
                if (fromPatientDetail && !currentTratamiento) {
                    // Si se creó un nuevo tratamiento desde el detalle del paciente, regresar
                    handleBackToPatientDetail();
                } else {
                    // Recargar la lista de tratamientos
                    await fetchTratamientos();
                    resetForm();
                }
            } else {
                alert('Error: ' + result.message);
            }
        } catch (err) {
            console.error('Error al procesar tratamiento:', err);
            alert('Error al procesar la solicitud');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            MEDICO_ID: '',
            PACIENTE_ID: pacienteId || '',
            TRATAMIENTO_FECHA_INICIO: '',
            TRATAMIENTO_FECHA_FIN: '',
            TRATAMIENTO_DESCRIPCION: ''
        });
        setMedicamentos([{ id: null, nombre: '', cantidad: '', unidad: 'mg' }]);
        setMedicamentosEliminados([]); // Resetear medicamentos eliminados
        setCurrentTratamiento(null);
        setShowForm(false);
        setErrors({});
    };

    // Manejar edición
    const handleEdit = (tratamiento) => {
        console.log('📝 Editando tratamiento:', tratamiento);
        
        setCurrentTratamiento(tratamiento);
        
        // Formatear las fechas para el input type="date" (YYYY-MM-DD)
        const formatearFecha = (fecha) => {
            if (!fecha) return '';
            const date = new Date(fecha);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };
        
        setFormData({
            MEDICO_ID: tratamiento.MEDICO_ID || '',
            PACIENTE_ID: tratamiento.SOCIA_ID || '',
            TRATAMIENTO_FECHA_INICIO: formatearFecha(tratamiento.TRATAMIENTO_FECHAINICIO),
            TRATAMIENTO_FECHA_FIN: formatearFecha(tratamiento.TRATAMIENTO_FECHAFIN),
            TRATAMIENTO_DESCRIPCION: tratamiento.TRATAMIENTO_OBSERVACION || ''
        });
        
        // Cargar TODOS los medicamentos del tratamiento CON SUS IDs
        if (tratamiento.MEDICAMENTOS && Array.isArray(tratamiento.MEDICAMENTOS) && tratamiento.MEDICAMENTOS.length > 0) {
            const medicamentosEditados = tratamiento.MEDICAMENTOS.map(med => ({
                id: med.MEDICAMENTO_ID || null, // ⚠️ IMPORTANTE: Guardar el ID para edición
                nombre: med.MEDICAMENTO_NOMBRE || '',
                cantidad: med.MEDICAMENTO_CANTIDAD || '',
                unidad: med.MEDICAMENTO_UNIDAD || 'mg'
            }));
            setMedicamentos(medicamentosEditados);
        } else {
            // Fallback: un medicamento vacío
            setMedicamentos([{ id: null, nombre: '', cantidad: '', unidad: 'mg' }]);
        }
        
        setMedicamentosEliminados([]); // Resetear medicamentos eliminados al editar
        setShowForm(true);
        setErrors({});
    };

    // Manejar visualización
    const handleView = (tratamiento) => {
        setViewTratamiento(tratamiento);
        setShowViewModal(true);
    };

    // Manejar eliminación
    const handleDelete = async (id) => {
        if (window.confirm('¿Está seguro de que desea eliminar este tratamiento?')) {
            setLoading(true);
            try {
                const result = await tratamientoService.deleteTratamiento(id);
                if (result.success) {
                    alert(result.message);
                    // Recargar la lista de tratamientos
                    await fetchTratamientos();
                } else {
                    alert('Error: ' + result.message);
                }
            } catch (err) {
                console.error('Error al eliminar tratamiento:', err);
                alert('Error al eliminar el tratamiento');
            } finally {
                setLoading(false);
            }
        }
    };

    // Cancelar formulario
    const handleCancel = () => {
        resetForm();
    };

    // Navegación
    // Eliminada función de back no utilizada para cumplir ESLint (no-unused-vars)

    // Función para volver a la información de la socia después de crear tratamiento
    const handleBackToPatientDetail = () => {
        navigate('/partners');
        // TODO: Idealmente aquí abriríamos directamente el modal de la socia
    };

    // Formatear fecha para mostrar
    const formatDate = (dateString) => {
        if (!dateString) return 'No definida';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES');
    };

    // Calcular estado del tratamiento basado en fechas
    const getTratamientoEstado = (tratamiento) => {
        const hoy = new Date();
        const fechaInicio = new Date(tratamiento.TRATAMIENTO_FECHAINICIO || tratamiento.TRATAMIENTO_FECHA_INICIO);
        const fechaFin = new Date(tratamiento.TRATAMIENTO_FECHAFIN || tratamiento.TRATAMIENTO_FECHA_FIN);
        
        if (hoy < fechaInicio) {
            return { estado: 'Programado', class: 'estado-programado' };
        } else if (hoy >= fechaInicio && hoy <= fechaFin) {
            return { estado: 'En Curso', class: 'estado-activo' };
        } else {
            return { estado: 'Finalizado', class: 'estado-finalizado' };
        }
    };

    if (loading) {
        return (
            <div className="inventory-management-container">
                <div className="inventory-management-header">
                    <h1>Gestión de Tratamientos</h1>
                    <p>Cargando datos de tratamientos médicos...</p>
                </div>
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    Cargando...
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="inventory-management-container">
                <div className="inventory-management-header">
                    <h1>Gestión de Tratamientos</h1>
                    <p style={{ color: 'red' }}>Error: {error}</p>
                </div>
                <button onClick={fetchTratamientos}>Reintentar</button>
            </div>
        );
    }

    if (showForm) {
        return (
            <div className="inventory-management-container">
                <div className="inventory-management-header">
                    <h1>Gestión de Tratamientos</h1>
                    <p>
                        {pacienteNombre 
                            ? `Administrar tratamientos médicos para ${pacienteNombre}` 
                            : 'Administrar tratamientos médicos de FEPAST'
                        }
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="inventory-form">
                    <div className="form-section">
                        <h3>
                            <FaPills />
                            {currentTratamiento ? 'Editar Tratamiento' : 'Nuevo Tratamiento'}
                        </h3>
                        
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="MEDICO_ID">Médico Responsable</label>
                                <select
                                    id="MEDICO_ID"
                                    name="MEDICO_ID"
                                    value={formData.MEDICO_ID}
                                    onChange={handleChange}
                                    className={errors.MEDICO_ID ? 'error' : ''}
                                    disabled={currentTratamiento} // Solo lectura en edición
                                >
                                    <option value="">Seleccionar médico...</option>
                                    {medicos.map(medico => (
                                        <option key={medico.MEDICO_ID} value={medico.MEDICO_ID}>
                                            {medico.MEDICO_NOMBRE} {medico.MEDICO_APELLIDO} - {medico.ESPECIALIDAD}
                                        </option>
                                    ))}
                                </select>
                                {errors.MEDICO_ID && <span className="error-message">{errors.MEDICO_ID}</span>}
                            </div>
                            
                            <div className="form-group">
                                <label htmlFor="PACIENTE_ID">Paciente</label>
                                <select
                                    id="PACIENTE_ID"
                                    name="PACIENTE_ID"
                                    value={formData.PACIENTE_ID}
                                    onChange={handleChange}
                                    className={errors.PACIENTE_ID ? 'error' : ''}
                                    disabled={currentTratamiento || pacienteId} // Solo lectura en edición o si viene de un paciente específico
                                >
                                    <option value="">Seleccionar paciente...</option>
                                    {pacientes.map(paciente => (
                                        <option key={paciente.PACIENTE_ID} value={paciente.PACIENTE_ID}>
                                            {paciente.PACIENTE_NOMBRE} - {paciente.PACIENTE_CEDULA}
                                        </option>
                                    ))}
                                </select>
                                {errors.PACIENTE_ID && <span className="error-message">{errors.PACIENTE_ID}</span>}
                            </div>
                        </div>

                        {/* Sección de Medicamentos */}
                        <div className="form-row" style={{ display: 'block' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                <label style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>
                                    <FaPills style={{ marginRight: '0.5rem' }} />
                                    Medicamentos
                                </label>
                                <button
                                    type="button"
                                    onClick={handleAddMedicamento}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        padding: '0.5rem 1rem',
                                        backgroundColor: '#2E7D32',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontSize: '0.9rem',
                                        fontWeight: '500',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#1B5E20'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = '#2E7D32'}
                                >
                                    <FaPlus /> Agregar Medicamento
                                </button>
                            </div>

                            {medicamentos.map((medicamento, index) => (
                                <div 
                                    key={index} 
                                    style={{ 
                                        marginBottom: '1rem',
                                        padding: '1rem',
                                        border: '2px solid #E0E0E0',
                                        borderRadius: '8px',
                                        backgroundColor: '#FAFAFA'
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                                        <span style={{ fontWeight: '600', color: '#666' }}>
                                            Medicamento {index + 1}
                                        </span>
                                        {medicamentos.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveMedicamento(index)}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.3rem',
                                                    padding: '0.4rem 0.8rem',
                                                    backgroundColor: '#D32F2F',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    fontSize: '0.85rem',
                                                    transition: 'all 0.2s'
                                                }}
                                                onMouseEnter={(e) => e.target.style.backgroundColor = '#B71C1C'}
                                                onMouseLeave={(e) => e.target.style.backgroundColor = '#D32F2F'}
                                            >
                                                <FaTrash /> Eliminar
                                            </button>
                                        )}
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Nombre del Medicamento</label>
                                            <input
                                                type="text"
                                                value={medicamento.nombre}
                                                onChange={(e) => handleMedicamentoChange(index, 'nombre', e.target.value)}
                                                placeholder="Ej: Paracetamol, Ibuprofeno..."
                                                className={errors[`medicamento_nombre_${index}`] ? 'error' : ''}
                                            />
                                            {errors[`medicamento_nombre_${index}`] && (
                                                <span className="error-message">{errors[`medicamento_nombre_${index}`]}</span>
                                            )}
                                        </div>
                                        
                                        <div className="form-group">
                                            <label>Cantidad</label>
                                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'stretch' }}>
                                                <input
                                                    type="number"
                                                    value={medicamento.cantidad}
                                                    onChange={(e) => handleMedicamentoChange(index, 'cantidad', e.target.value)}
                                                    placeholder="0"
                                                    min="1"
                                                    className={errors[`medicamento_cantidad_${index}`] ? 'error' : ''}
                                                    style={{ flex: '2' }}
                                                />
                                                <select
                                                    value={medicamento.unidad}
                                                    onChange={(e) => handleMedicamentoChange(index, 'unidad', e.target.value)}
                                                    style={{ 
                                                        flex: '1',
                                                        padding: '0.875rem 1rem',
                                                        border: '2px solid #E0E0E0',
                                                        borderRadius: '8px',
                                                        fontSize: '1rem',
                                                        fontFamily: 'inherit',
                                                        backgroundColor: 'white',
                                                        minWidth: '70px'
                                                    }}
                                                >
                                                    <option value="mg">mg</option>
                                                    <option value="g">g</option>
                                                </select>
                                            </div>
                                            {errors[`medicamento_cantidad_${index}`] && (
                                                <span className="error-message">{errors[`medicamento_cantidad_${index}`]}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="TRATAMIENTO_FECHA_INICIO">Fecha de Inicio</label>
                                <input
                                    type="date"
                                    id="TRATAMIENTO_FECHA_INICIO"
                                    name="TRATAMIENTO_FECHA_INICIO"
                                    value={formData.TRATAMIENTO_FECHA_INICIO}
                                    onChange={handleChange}
                                    min={!currentTratamiento ? new Date().toISOString().split('T')[0] : undefined}
                                    className={errors.TRATAMIENTO_FECHA_INICIO ? 'error' : ''}
                                />
                                {errors.TRATAMIENTO_FECHA_INICIO && <span className="error-message">{errors.TRATAMIENTO_FECHA_INICIO}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="TRATAMIENTO_FECHA_FIN">Fecha de Fin</label>
                                <input
                                    type="date"
                                    id="TRATAMIENTO_FECHA_FIN"
                                    name="TRATAMIENTO_FECHA_FIN"
                                    value={formData.TRATAMIENTO_FECHA_FIN}
                                    onChange={handleChange}
                                    className={errors.TRATAMIENTO_FECHA_FIN ? 'error' : ''}
                                />
                                {errors.TRATAMIENTO_FECHA_FIN && <span className="error-message">{errors.TRATAMIENTO_FECHA_FIN}</span>}
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="TRATAMIENTO_DESCRIPCION">Descripción e Instrucciones</label>
                                <textarea
                                    id="TRATAMIENTO_DESCRIPCION"
                                    name="TRATAMIENTO_DESCRIPCION"
                                    value={formData.TRATAMIENTO_DESCRIPCION}
                                    onChange={handleChange}
                                    placeholder="Describe las instrucciones del tratamiento, posología, recomendaciones..."
                                    rows="4"
                                    style={{ 
                                        width: '100%', 
                                        padding: '0.875rem 1rem',
                                        border: '2px solid #E0E0E0',
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        fontFamily: 'inherit',
                                        resize: 'vertical'
                                    }}
                                />
                            </div>
                        </div>

                        {currentTratamiento && (
                            <div className="edit-note">
                                <p><strong>Nota:</strong> En modo edición no se pueden modificar el médico responsable ni el paciente.</p>
                            </div>
                        )}

                        {pacienteNombre && (
                            <div className="edit-note">
                                <p><strong>Paciente seleccionado:</strong> {pacienteNombre}</p>
                            </div>
                        )}
                    </div>

                    <div className="form-actions">
                        <button type="button" className="btn-secondary" onClick={handleCancel}>
                            Cancelar
                        </button>
                        <button type="submit" className="btn-primary">
                            {currentTratamiento ? 'Actualizar' : 'Crear'} Tratamiento
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    return (
        <div className="inventory-management-container">
            <div className="inventory-management-header">
                <h1>Gestión de Tratamientos</h1>
                <p>
                    {pacienteNombre 
                        ? `Tratamientos médicos de ${pacienteNombre}` 
                        : 'Administrar todos los tratamientos médicos de FEPAST'
                    }
                </p>
            </div>

            <div className="inventory-management-actions">
                <div className="search-section">
                    <FaSearch className="search-icon" />
                    <input
                        type="text"
                        placeholder="Buscar por ID, médico, paciente, medicamento..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                </div>

                <div className="action-buttons">
                    <button className="register-btn" onClick={() => setShowForm(true)}>
                        <FaPlus /> Nuevo Tratamiento
                    </button>
                </div>
            </div>

            <div className="inventory-table-container">
                <table className="inventory-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Médico</th>
                            <th>Paciente</th>
                            <th>Medicamento</th>
                            <th>Cantidad</th>
                            <th>Periodo</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentItems.map(tratamiento => {
                            const estadoInfo = getTratamientoEstado(tratamiento);
                            return (
                                <tr key={tratamiento.TRATAMIENTO_ID}>
                                    <td><span className="product-id">{tratamiento.TRATAMIENTO_ID}</span></td>
                                    <td>
                                        <div className="product-name">
                                            <div className="product-icon" style={{ background: '#E8F5E8', color: '#2E7D32' }}>
                                                <FaUserMd />
                                            </div>
                                            <div>
                                                <div>{tratamiento.MEDICO_NOMBRE || tratamiento.medico_nombre} {tratamiento.MEDICO_APELLIDO || tratamiento.medico_apellido}</div>
                                                <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                                    {tratamiento.MEDICO_ESPECIALIDAD || tratamiento.especialidad_nombre || 'Sin especialidad'}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="category-info">
                                            <span>{tratamiento.PACIENTE_NOMBRE || tratamiento.SOCIA_ID}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="location-info">
                                            <FaPills className="location-icon" style={{ color: '#6F42C1' }} />
                                            <span>
                                                {tratamiento.MEDICAMENTOS && tratamiento.MEDICAMENTOS.length > 0 
                                                    ? `${tratamiento.MEDICAMENTOS[0].MEDICAMENTO_NOMBRE}${tratamiento.MEDICAMENTOS.length > 1 ? ` +${tratamiento.MEDICAMENTOS.length - 1} más` : ''}`
                                                    : tratamiento.MEDICAMENTO_NOMBRE || 'Sin medicamento'}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className="quantity-badge">
                                            {tratamiento.MEDICAMENTOS && tratamiento.MEDICAMENTOS.length > 0
                                                ? `${tratamiento.MEDICAMENTOS[0].MEDICAMENTO_CANTIDAD} ${tratamiento.MEDICAMENTOS[0].MEDICAMENTO_UNIDAD || 'mg'}`
                                                : tratamiento.MEDICAMENTO_CANTIDAD 
                                                    ? `${tratamiento.MEDICAMENTO_CANTIDAD} ${tratamiento.MEDICAMENTO_UNIDAD || 'mg'}`
                                                    : '-'}
                                        </span>
                                    </td>
                                    <td>
                                        <div style={{ fontSize: '0.85rem' }}>
                                            <div><strong>Inicio:</strong> {formatDate(tratamiento.TRATAMIENTO_FECHAINICIO || tratamiento.TRATAMIENTO_FECHA_INICIO)}</div>
                                            <div><strong>Fin:</strong> {formatDate(tratamiento.TRATAMIENTO_FECHAFIN || tratamiento.TRATAMIENTO_FECHA_FIN)}</div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`estado-badge ${estadoInfo.class}`}>
                                            {estadoInfo.estado}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="actions">
                                            <button
                                                className="action-btn"
                                                onClick={() => handleView(tratamiento)}
                                                title="Ver detalles"
                                                style={{ background: '#F0F8FF', color: '#1976D2' }}
                                            >
                                                <FaEye />
                                            </button>
                                            <button
                                                className="action-btn edit-btn"
                                                onClick={() => handleEdit(tratamiento)}
                                                title="Editar tratamiento"
                                            >
                                                <FaEdit />
                                            </button>
                                            <button
                                                className="action-btn delete-btn"
                                                onClick={() => handleDelete(tratamiento.TRATAMIENTO_ID)}
                                                title="Eliminar tratamiento"
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {currentItems.length === 0 && (
                    <div className="table-footer">
                        {pacienteNombre 
                            ? `No se encontraron tratamientos para ${pacienteNombre}` 
                            : 'No se encontraron tratamientos médicos'
                        }
                    </div>
                )}
            </div>

            <div className="records-info" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginTop: '1rem' }}>
                <div>
                    Mostrando <strong>{serverSide ? tratamientos.length : currentItems.length}</strong> de <strong>{serverSide ? totalItems : filteredTratamientos.length}</strong> tratamientos{pacienteNombre ? ` para ${pacienteNombre}` : ''}
                </div>

                <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button className="btn-secondary" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Anterior</button>
                    <span> Página <strong>{currentPage}</strong> de <strong>{totalPages}</strong> </span>
                    <button className="btn-secondary" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Siguiente</button>
                    <label style={{ marginLeft: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>Por página:</span>
                        <select value={itemsPerPage} onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                        </select>
                    </label>
                </div>
            </div>

            {/* Modal de visualización de detalles */}
            {showViewModal && viewTratamiento && (
                <div className="modal" style={{ display: 'flex' }}>
                    <div className="modal-content" style={{ maxWidth: '600px', margin: 'auto' }}>
                        <div className="modal-header">
                            <h2>Detalles del Tratamiento {viewTratamiento.TRATAMIENTO_ID}</h2>
                            <button onClick={() => setShowViewModal(false)} className="close-btn">
                                ×
                            </button>
                        </div>
                        <div className="modal-body">
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div>
                                    <h4><FaUserMd /> Información Médica</h4>
                                    <p><strong>Médico:</strong> {viewTratamiento.MEDICO_NOMBRE || viewTratamiento.medico_nombre} {viewTratamiento.MEDICO_APELLIDO || viewTratamiento.medico_apellido}</p>
                                    <p><strong>Especialidad:</strong> {viewTratamiento.MEDICO_ESPECIALIDAD || viewTratamiento.especialidad_nombre || 'Sin especialidad'}</p>
                                    <p><strong>Paciente:</strong> {viewTratamiento.PACIENTE_NOMBRE || viewTratamiento.SOCIA_ID}</p>
                                </div>
                                <div>
                                    <h4><FaPills /> Medicamentos</h4>
                                    {viewTratamiento.MEDICAMENTOS && viewTratamiento.MEDICAMENTOS.length > 0 ? (
                                        viewTratamiento.MEDICAMENTOS.map((med, index) => (
                                            <div key={index} style={{ marginBottom: '0.5rem', padding: '0.5rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                                                <p style={{ margin: 0 }}><strong>{med.MEDICAMENTO_NOMBRE}</strong></p>
                                                <p style={{ margin: 0, fontSize: '0.9rem', color: '#666' }}>
                                                    {med.MEDICAMENTO_CANTIDAD} {med.MEDICAMENTO_UNIDAD || 'mg'}
                                                </p>
                                            </div>
                                        ))
                                    ) : (
                                        <>
                                            <p><strong>Medicamento:</strong> {viewTratamiento.MEDICAMENTO_NOMBRE || 'Sin medicamento'}</p>
                                            {viewTratamiento.MEDICAMENTO_CANTIDAD && (
                                                <p><strong>Cantidad:</strong> {viewTratamiento.MEDICAMENTO_CANTIDAD} {viewTratamiento.MEDICAMENTO_UNIDAD || 'mg'}</p>
                                            )}
                                        </>
                                    )}
                                    <p><strong>Estado:</strong> {getTratamientoEstado(viewTratamiento).estado}</p>
                                </div>
                            </div>
                            <div style={{ marginTop: '1rem' }}>
                                <h4><FaCalendarAlt /> Período del Tratamiento</h4>
                                <p><strong>Fecha de Inicio:</strong> {formatDate(viewTratamiento.TRATAMIENTO_FECHAINICIO || viewTratamiento.TRATAMIENTO_FECHA_INICIO)}</p>
                                <p><strong>Fecha de Fin:</strong> {formatDate(viewTratamiento.TRATAMIENTO_FECHAFIN || viewTratamiento.TRATAMIENTO_FECHA_FIN)}</p>
                            </div>
                            <div style={{ marginTop: '1rem' }}>
                                <h4>Descripción e Instrucciones</h4>
                                <p style={{ background: '#f5f5f5', padding: '1rem', borderRadius: '8px', whiteSpace: 'pre-wrap' }}>
                                    {viewTratamiento.TRATAMIENTO_OBSERVACION || viewTratamiento.TRATAMIENTO_DESCRIPCION || 'Sin descripción'}
                                </p>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button onClick={() => setShowViewModal(false)} className="btn-secondary">
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TratamientoManagement;